// Both transports may split UTF-8, JSON escapes, and SSE frames anywhere.
export async function* readAutotranslatorSse(body: ReadableStream<Uint8Array>) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let pending = "";
    try {
        while (true) {
            const { value, done } = await reader.read();
            pending += done ? decoder.decode() : decoder.decode(value, { stream: true });
            pending = pending.replace(/\r\n/g, "\n");
            if (pending.length > 131072) throw new Error("Translation stream frame too large.");
            let end: number;
            while ((end = pending.indexOf("\n\n")) >= 0) {
                const frame = pending.slice(0, end);
                pending = pending.slice(end + 2);
                const data = frame.split("\n").filter(line => line.startsWith("data:"))
                    .map(line => line.slice(5).trimStart()).join("\n");
                if (data && data !== "[DONE]") yield JSON.parse(data) as Record<string, unknown>;
            }
            if (done) {
                if (pending.trim()) throw new Error("Truncated translation stream.");
                return;
            }
        }
    } finally {
        await reader.cancel().catch(() => undefined);
        reader.releaseLock();
    }
}

// Read only top-level string fields. Never leak JSON syntax or incomplete escapes
// into a caption, and never mistake quoted content for a property name.
export const readAutotranslatorPartialString = (json: string, key: string): string => {
    let index = 0;
    const skipSpace = () => { while (/\s/.test(json[index] ?? "") && index < json.length) index++; };
    const readString = (): { text: string; complete: boolean } => {
        index++;
        let text = "";
        while (index < json.length) {
            const character = json[index++];
            if (character === '"') return { text, complete: true };
            if (character !== "\\") { text += character; continue; }
            const escape = json[index++];
            if (!escape) break;
            if (escape === "u") {
                const hex = json.slice(index, index + 4);
                if (!/^[0-9a-f]{4}$/i.test(hex)) break;
                text += String.fromCharCode(parseInt(hex, 16));
                index += 4;
            } else {
                const escapes: Record<string, string> = { '"': '"', "\\": "\\", "/": "/", n: "\n", r: "\r", t: "\t", b: "\b", f: "\f" };
                if (!(escape in escapes)) break;
                text += escapes[escape];
            }
        }
        return { text: text.replace(/[\uD800-\uDBFF]$/, ""), complete: false };
    };
    skipSpace();
    if (json[index++] !== "{") return "";
    while (index < json.length) {
        skipSpace();
        if (json[index] === ",") { index++; skipSpace(); }
        if (json[index] !== '"') return "";
        const name = readString();
        if (!name.complete) return "";
        skipSpace();
        if (json[index++] !== ":") return "";
        skipSpace();
        if (json[index] === '"') {
            const value = readString();
            if (name.text === key) return value.text;
            if (!value.complete) return "";
        } else {
            while (index < json.length && json[index] !== "," && json[index] !== "}") index++;
        }
    }
    return "";
};
